import type { ApiResponse } from "apisauce"

import { request } from "../../../services/api"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { pickFormError } from "../../../utils/handlers/pick-form-error.ts"
import type {
  EnvelopeCreateBody,
  EnvelopeSlot,
  EnvelopeUpdateBody,
  FieldCreateBody,
  FieldPatchBody,
  Readiness,
  SignatureField,
  SigningEnvelope
} from "../types/esign.types.ts"

const BASE_URL = "/esign/envelopes"

const PATHS = {
  envelopes: `${BASE_URL}/`,
  envelope: (id: string) => `${BASE_URL}/${id}/`,
  fields: (id: string) => `${BASE_URL}/${id}/fields/`,
  field: (id: string, fieldId: string) =>
    `${BASE_URL}/${id}/fields/${fieldId}/`,
  readiness: (id: string) => `${BASE_URL}/${id}/readiness/`,
  slots: (id: string) => `${BASE_URL}/${id}/slots/`,
  send: (id: string) => `${BASE_URL}/${id}/send/`,
  document: (id: string) => `${BASE_URL}/${id}/document/`
}

export interface EsignResult<T> {
  ok: boolean
  status: number | null
  data: T | null
  errors: Record<string, string[]> | null
  message: string | null
  /** `scanner_unavailable`, `draft_exists`, … when the server names its reason. */
  code: string | null
  /** The existing draft a `draft_exists` refusal points to. */
  envelopeId: string | null
}

const NOTHING_REFUSED = {
  errors: null,
  message: null,
  code: null,
  envelopeId: null
} as const

const stringAt = (body: unknown, key: "code" | "envelope_id") => {
  const value = (body as Record<string, unknown> | null | undefined)?.[key]
  return typeof value === "string" ? value : null
}

const succeeded = <T>(status: number | null, data: T | null) => ({
  ok: true,
  status,
  data,
  ...NOTHING_REFUSED
})

const toResult = <T>(response: ApiResponse<unknown>): EsignResult<T> => {
  const status = response.status ?? null
  if (response.ok) return succeeded(status, unwrapEnvelope<T>(response.data))
  const picked = pickFormError(response.data)
  return {
    ok: false,
    status,
    data: null,
    errors: picked && typeof picked === "object" ? picked : null,
    message: typeof picked === "string" ? picked : null,
    code: stringAt(response.data, "code"),
    envelopeId: stringAt(response.data, "envelope_id")
  }
}

export const firstErrorMessage = (
  result: Pick<EsignResult<unknown>, "errors" | "message">
): string | null => {
  if (result.message) return result.message
  const first = Object.values(result.errors ?? {})[0]
  return first?.[0] ?? null
}

export const createEnvelope = async (body: EnvelopeCreateBody) =>
  toResult<SigningEnvelope>(await request.api.post(PATHS.envelopes, body))

interface EnvelopePage {
  results: SigningEnvelope[]
}

export const listLeadEnvelopes = async (
  leadId: string
): Promise<EsignResult<SigningEnvelope[]>> => {
  const result = await toResult<EnvelopePage | null>(
    await request.api.get(PATHS.envelopes, { lead: leadId, page_size: 100 })
  )
  return { ...result, data: result.data?.results ?? null }
}

export const getEnvelope = async (id: string) =>
  toResult<SigningEnvelope>(await request.api.get(PATHS.envelope(id)))

export const updateEnvelope = async (id: string, body: EnvelopeUpdateBody) =>
  toResult<SigningEnvelope>(await request.api.patch(PATHS.envelope(id), body))

export const getEnvelopeSlots = async (id: string) =>
  toResult<EnvelopeSlot[]>(await request.api.get(PATHS.slots(id)))

export const getEnvelopeFields = async (id: string) =>
  toResult<SignatureField[]>(await request.api.get(PATHS.fields(id)))

export const createEnvelopeField = async (id: string, body: FieldCreateBody) =>
  toResult<SignatureField>(await request.api.post(PATHS.fields(id), body))

export const updateEnvelopeField = async (
  id: string,
  fieldId: string,
  body: FieldPatchBody
) =>
  toResult<SignatureField>(
    await request.api.patch(PATHS.field(id, fieldId), body)
  )

export const deleteEnvelopeField = async (id: string, fieldId: string) =>
  toResult<null>(await request.api.delete(PATHS.field(id, fieldId)))

export const getEnvelopeReadiness = async (id: string) =>
  toResult<Readiness>(await request.api.get(PATHS.readiness(id)))

export const sendEnvelope = async (id: string) =>
  toResult<SigningEnvelope>(await request.api.post(PATHS.send(id)))

export const getEnvelopeDocument = async (
  id: string
): Promise<EsignResult<Uint8Array>> => {
  const response = await request.api.get<ArrayBuffer>(
    PATHS.document(id),
    {},
    { responseType: "arraybuffer" }
  )
  const status = response.status ?? null
  if (!response.ok || !response.data) {
    return { ok: false, status, data: null, ...NOTHING_REFUSED }
  }
  return succeeded(status, new Uint8Array(response.data))
}
